import { Icon } from '@latitude/icons';
import { cn } from '@latitude/token-utils';
import {
  Indicator as CheckboxIndicator,
  Root as CheckboxRoot,
} from '@radix-ui/react-checkbox';
import type { MouseEvent, Ref } from 'react';
import { useEffect, useId, useState } from 'react';
import type { CheckboxProps, CheckedState } from 'types';
import { checkboxItemClasses } from 'variants/checkbox-variants';

import { CheckboxLabel } from './CheckboxLabel';

const getNextCheckedStateOnContainerClick = (
  event: MouseEvent<HTMLDivElement>,
  isChecked: CheckedState,
  disableWrapperClick: boolean,
): CheckedState | null => {
  // Only toggle if clicking on the wrapper itself or the label
  // Don't toggle if clicking the checkbox button or input (they handle their own state)
  const target = event.target as HTMLElement;
  const currentTarget = event.currentTarget as HTMLElement;

  // Check if the click target is the checkbox button or an input
  const isCheckboxButton = target.closest('button[role="checkbox"]');
  const isInput = target.tagName === 'INPUT';

  // If wrapper clicks are disabled, do nothing
  // If clicking the checkbox button or input, don't toggle (let them handle it)
  if (disableWrapperClick || isCheckboxButton || isInput) {
    return null;
  }

  // Otherwise, check if we're clicking the wrapper or label (not the checkbox)
  // Only toggle for clicks on the wrapper/label, not for clicks that bubbled from the checkbox
  const isWrapperOrLabelClick =
    target === currentTarget || target.tagName === 'LABEL';

  if (isWrapperOrLabelClick) {
    // Toggle the checkbox state
    return typeof isChecked === 'string' ? true : !isChecked;
  }

  return null;
};

const getNextState = (
  indeterminate: boolean | undefined,
  checked: CheckedState,
): CheckedState => (indeterminate ? 'indeterminate' : checked);

export const Checkbox = ({
  id,
  className,
  indeterminate,
  onCheckedChange,
  checked = false,
  label,
  children,
  wrapperClassname = '',
  labelClassname = '',
  disabled,
  rawProps = {},
  invalid = false,
  childrenBefore = false,
  wrapperProps = {},
  disableWrapperClick = false,
  stopPropagation = false,
  hideCheckbox,
  ref,
  ...props
}: CheckboxProps & {
  ref?: Ref<HTMLButtonElement>;
}) => {
  const defaultId = useId();
  const idToUse = id ?? defaultId;
  const [isChecked, setIsChecked] = useState<CheckedState>(
    getNextState(indeterminate, checked),
  );

  useEffect(() => {
    setIsChecked(getNextState(indeterminate, checked));
  }, [checked, indeterminate]);

  const handleCheckedChange = (nextChecked: CheckedState) => {
    setIsChecked(nextChecked);
    if (onCheckedChange) {
      onCheckedChange(nextChecked);
    }
  };

  const handleContainerClick = (event: MouseEvent<HTMLDivElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
      event.preventDefault();
    }

    const nextChecked = getNextCheckedStateOnContainerClick(
      event,
      isChecked,
      disableWrapperClick,
    );

    if (disabled) {
      return;
    }

    if (nextChecked !== null) {
      handleCheckedChange(nextChecked);
    }
  };

  return (
    <div
      className={cn(['flex', 'items-center', wrapperClassname])}
      onClick={handleContainerClick}
      onKeyDown={(event) => {
        if (stopPropagation) {
          event.stopPropagation();
          event.preventDefault();
        }
      }}
      {...wrapperProps}
    >
      {childrenBefore && children}
      <CheckboxRoot
        ref={ref}
        className={cn(checkboxItemClasses, className)}
        onCheckedChange={handleCheckedChange}
        checked={isChecked}
        id={idToUse}
        disabled={disabled}
        aria-invalid={invalid}
        hidden={hideCheckbox}
        {...props}
        {...rawProps}
      >
        <CheckboxIndicator forceMount>
          {isChecked === 'indeterminate' && <Icon name="checkbox-indeterm" />}
          {isChecked === false && <Icon name="checkbox-uncheck" />}
          {isChecked === true && <Icon name="checkbox-check" />}
        </CheckboxIndicator>
      </CheckboxRoot>
      {label ? (
        <CheckboxLabel
          htmlFor={idToUse}
          className={cn(['pointer-events-none', labelClassname])}
        >
          {label}
        </CheckboxLabel>
      ) : null}
      {!childrenBefore && children}
    </div>
  );
};

Checkbox.displayName = 'Checkbox';
